<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Header -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border-b px-6 py-4'
      ]"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <!-- Back Button -->
          <button
            class="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            @click="$router.push('/quality-gates')"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span>Back to Quality Gates</span>
          </button>

          <!-- PR Info -->
          <div v-if="prDetails">
            <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
              {{ prDetails.repository }} #{{ prDetails.pr_number }}
            </h1>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Last updated {{ qualityGatesService.formatTimestamp(prDetails.last_updated) }}
            </p>
          </div>
        </div>

        <div class="flex items-center space-x-3">
          <!-- Re-trigger Button -->
          <button
            :disabled="retriggering"
            :class="[
              theme.isDark.value
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-blue-600 hover:bg-blue-700',
              'px-4 py-2 text-white rounded-md text-sm font-medium transition-colors flex items-center',
              retriggering ? 'opacity-50 cursor-not-allowed' : ''
            ]"
            @click="showRetriggerModal = true"
          >
            <svg
              :class="[retriggering ? 'animate-spin' : '', 'w-4 h-4 mr-2']"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {{ retriggering ? 'Triggering...' : 'Re-trigger Gates' }}
          </button>

          <!-- Refresh Button -->
          <button
            :disabled="loading"
            :class="[
              theme.isDark.value
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                : 'bg-white hover:bg-gray-50 text-gray-700',
              'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors'
            ]"
            @click="refreshDetails"
          >
            Refresh
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="loading && !prDetails"
      class="p-6"
    >
      <div
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-8'
        ]"
      >
        <div class="flex items-center justify-center">
          <svg
            class="animate-spin h-8 w-8 text-blue-500"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span class="ml-3 text-lg text-gray-600 dark:text-gray-300">Loading PR details...</span>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="p-6"
    >
      <div
        class="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-md p-4"
      >
        <div class="flex">
          <svg
            class="h-5 w-5 text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800 dark:text-red-200">
              Error loading PR details
            </h3>
            <div class="mt-2 text-sm text-red-700 dark:text-red-300">{{ error }}</div>
            <NetworkAccessHint />
            <button
              class="mt-2 px-3 py-1 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-100 rounded text-sm"
              @click="refreshDetails"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- PR Details Content -->
    <div
      v-else-if="prDetails"
      class="p-6 space-y-6"
    >
      <!-- Summary Card -->
      <div
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-6',
          `border-l-4 ${qualityGatesService.getStatusBorderColor(prDetails.status)}`
        ]"
      >
        <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
          <!-- Status -->
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400 mb-1">Status</div>
            <span
              :class="[
                qualityGatesService.getStatusColor(prDetails.status || 'unknown'),
                'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium'
              ]"
            >
              <span class="mr-1">{{
                qualityGatesService.getStatusIcon(prDetails.status || 'unknown')
              }}</span>
              {{ (prDetails.status || 'UNKNOWN').toUpperCase() }}
            </span>
          </div>

          <!-- Commit -->
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400 mb-1">Commit</div>
            <a
              v-if="prDetails.commit_sha"
              :href="`https://github.com/GoHighLevel/${prDetails.repository}/commit/${prDetails.commit_sha}`"
              target="_blank"
              class="font-mono text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              {{ prDetails.commit_sha.substring(0, 7) }}
            </a>
            <span
              v-else
              class="text-sm text-gray-400"
              >N/A</span
            >
          </div>

          <!-- Errors -->
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400 mb-1">Errors</div>
            <div class="text-xl font-bold text-red-600 dark:text-red-400">
              {{ prDetails.total_errors ?? 0 }}
            </div>
          </div>

          <!-- Warnings -->
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400 mb-1">Warnings</div>
            <div class="text-xl font-bold text-yellow-600 dark:text-yellow-400">
              {{ prDetails.total_warnings ?? 0 }}
            </div>
          </div>

          <!-- Gates -->
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400 mb-1">Gates Run</div>
            <div class="text-xl font-bold text-gray-900 dark:text-white">
              {{ totalGates }}
            </div>
          </div>
        </div>
      </div>

      <!-- Filter Controls -->
      <div
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-4'
        ]"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <span class="text-sm text-gray-500 dark:text-gray-400">Show:</span>
            <label class="flex items-center space-x-2 text-sm">
              <input
                v-model="showFailures"
                type="checkbox"
                class="rounded text-red-500 focus:ring-red-500"
              />
              <span class="text-red-600 dark:text-red-400">Failures</span>
            </label>
            <label class="flex items-center space-x-2 text-sm">
              <input
                v-model="showWarnings"
                type="checkbox"
                class="rounded text-yellow-500 focus:ring-yellow-500"
              />
              <span class="text-yellow-600 dark:text-yellow-400">Warnings</span>
            </label>
            <label class="flex items-center space-x-2 text-sm">
              <input
                v-model="showPassing"
                type="checkbox"
                class="rounded text-green-500 focus:ring-green-500"
              />
              <span class="text-green-600 dark:text-green-400">Passing</span>
            </label>
          </div>
          <button
            class="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            @click="expandAll = !expandAll"
          >
            {{ expandAll ? 'Collapse All' : 'Expand All' }}
          </button>
        </div>
      </div>

      <!-- Gate Results -->
      <div class="space-y-4">
        <div
          v-for="gate in filteredGates"
          :key="gate.key"
          :class="[
            theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg overflow-hidden'
          ]"
        >
          <!-- Gate Header -->
          <button
            class="w-full px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
            :class="[theme.isDark.value ? 'hover:bg-gray-700' : 'hover:bg-gray-50']"
            @click="toggleGate(gate.key)"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <span
                  :class="[
                    qualityGatesService.getStatusColor(gate.result.status),
                    'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium'
                  ]"
                >
                  {{ qualityGatesService.getStatusIcon(gate.result.status) }}
                </span>
                <div class="flex items-center">
                  <span class="font-medium text-gray-900 dark:text-white">
                    {{ qualityGatesService.formatGateName(gate.script) }}
                  </span>
                  <a
                    v-if="qualityGatesService.hasGateDocumentation(gate.script)"
                    :href="qualityGatesService.getGateDocumentationUrl(gate.script)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="ml-2 text-xs text-blue-600 dark:text-blue-400 hover:underline hover:text-blue-700 dark:hover:text-blue-300"
                    @click.stop
                  >
                    [docs]
                  </a>
                  <span
                    v-if="gate.team"
                    class="ml-2 text-sm text-gray-500 dark:text-gray-400"
                  >
                    ({{ gate.team }})
                  </span>
                </div>
              </div>
              <div class="flex items-center space-x-4">
                <!-- Block Date Badge -->
                <span
                  v-if="gate.result.summary?.blockDate"
                  :class="[
                    qualityGatesService.getBlockDateInfo(gate.result.summary.blockDate).colorClass,
                    'text-xs flex items-center'
                  ]"
                >
                  <svg
                    class="w-3.5 h-3.5 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  {{ qualityGatesService.getBlockDateInfo(gate.result.summary.blockDate).text }}
                </span>
                <div class="flex items-center space-x-2 text-sm">
                  <span
                    v-if="gate.result.summary?.errorCount"
                    class="text-red-600 dark:text-red-400"
                  >
                    {{ gate.result.summary.errorCount }} errors
                  </span>
                  <span
                    v-if="gate.result.summary?.warningCount"
                    class="text-yellow-600 dark:text-yellow-400"
                  >
                    {{ gate.result.summary.warningCount }} warnings
                  </span>
                </div>
                <svg
                  :class="[
                    'w-5 h-5 text-gray-400 transform transition-transform',
                    expandedGates.has(gate.key) ? 'rotate-180' : ''
                  ]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </button>

          <!-- Gate Details / Violations -->
          <div
            v-show="expandedGates.has(gate.key) || expandAll"
            class="border-t border-gray-200 dark:border-gray-700"
          >
            <div
              v-if="gate.result.details?.issues && gate.result.details.issues.length > 0"
              class="divide-y divide-gray-200 dark:divide-gray-700"
            >
              <div
                v-for="(violation, vIndex) in gate.result.details.issues"
                :key="vIndex"
                class="px-4 py-3"
                :class="[theme.isDark.value ? 'bg-gray-850' : 'bg-gray-50']"
              >
                <div class="flex items-start justify-between">
                  <div class="flex-1">
                    <div class="flex items-center space-x-2">
                      <span
                        :class="[
                          violation.severity?.toLowerCase() === 'error'
                            ? 'text-red-600 dark:text-red-400'
                            : violation.severity?.toLowerCase() === 'warning'
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-blue-600 dark:text-blue-400',
                          'text-xs font-medium uppercase'
                        ]"
                      >
                        {{ violation.severity }}
                      </span>
                      <span
                        v-if="violation.ruleId"
                        class="text-xs text-gray-500 dark:text-gray-400 font-mono"
                      >
                        {{ violation.ruleId }}
                      </span>
                    </div>
                    <p class="mt-1 text-sm text-gray-900 dark:text-white">
                      {{ violation.message }}
                    </p>
                    <a
                      v-if="violation.file && violation.line"
                      :href="
                        qualityGatesService.getViolationLink(
                          prDetails.repository,
                          prDetails.commit_sha,
                          violation.file,
                          violation.line
                        )
                      "
                      target="_blank"
                      class="mt-1 text-xs font-mono text-blue-600 dark:text-blue-400 hover:underline flex items-center"
                    >
                      {{ violation.file }}:{{ violation.line }}
                      <svg
                        class="w-3 h-3 ml-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else
              class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 italic"
            >
              No violations found
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State for Filtered Gates -->
      <div
        v-if="filteredGates.length === 0"
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-8 text-center'
        ]"
      >
        <p class="text-gray-500 dark:text-gray-400">No gates match the current filters.</p>
      </div>
    </div>

    <!-- Re-trigger Confirmation Modal -->
    <div
      v-if="showRetriggerModal"
      class="fixed inset-0 z-50 overflow-y-auto"
    >
      <div class="flex items-center justify-center min-h-screen px-4">
        <div
          class="fixed inset-0 bg-black opacity-50"
          @click="showRetriggerModal = false"
        ></div>
        <div
          :class="[
            theme.isDark.value ? 'bg-gray-800' : 'bg-white',
            'relative rounded-lg shadow-xl max-w-md w-full p-6'
          ]"
        >
          <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Re-trigger Quality Gates
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Are you sure you want to re-trigger quality gates for this PR?
          </p>
          <div
            class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 mb-4"
          >
            <div class="flex">
              <svg
                class="h-5 w-5 text-yellow-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <p class="ml-2 text-sm text-yellow-700 dark:text-yellow-200">
                Results will only change if the quality gate rules have been updated.
              </p>
            </div>
          </div>
          <div class="flex justify-end space-x-3">
            <button
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700',
                'px-4 py-2 rounded-md text-sm font-medium'
              ]"
              @click="showRetriggerModal = false"
            >
              Cancel
            </button>
            <button
              :disabled="retriggering"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
              @click="handleRetrigger"
            >
              {{ retriggering ? 'Triggering...' : 'Confirm Re-trigger' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useTheme } from '../../utils/useTheme'
import { qualityGatesService } from '../../services/quality-gates'
import NetworkAccessHint from '../../components/shared/NetworkAccessHint.vue'
import type { QualityGatePRDetails, GateResult, GateStatus } from '../../types/quality-gates'
import { logger } from '../../utils/logger'

const route = useRoute()
const theme = inject('theme', useTheme())

// State
const prDetails = ref<QualityGatePRDetails | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const retriggering = ref(false)
const showRetriggerModal = ref(false)

// Filter state
const showFailures = ref(true)
const showWarnings = ref(true)
const showPassing = ref(false)
const expandAll = ref(false)
const expandedGates = ref<Set<string>>(new Set())

// Route params
const repository = computed(() => decodeURIComponent(route.params.repo as string))
const prNumber = computed(() => parseInt(route.params.prNumber as string, 10))

// Flatten gate results for easier rendering
interface FlattenedGate {
  key: string
  script: string
  team: string
  result: GateResult
}

const flattenedGates = computed<FlattenedGate[]>(() => {
  if (!prDetails.value?.results) return []

  const gates: FlattenedGate[] = []
  for (const [script, teams] of Object.entries(prDetails.value.results)) {
    for (const [team, result] of Object.entries(teams)) {
      gates.push({
        key: `${script}-${team}`,
        script,
        team,
        result: result as GateResult
      })
    }
  }

  // Sort: failures first, then warnings, then success
  const statusOrder: Record<GateStatus, number> = { failure: 0, warning: 1, success: 2 }
  gates.sort((a, b) => statusOrder[a.result.status] - statusOrder[b.result.status])

  return gates
})

// Filter gates based on status toggles
const filteredGates = computed(() => {
  return flattenedGates.value.filter((gate) => {
    if (gate.result.status === 'failure' && showFailures.value) return true
    if (gate.result.status === 'warning' && showWarnings.value) return true
    if (gate.result.status === 'success' && showPassing.value) return true
    return false
  })
})

const totalGates = computed(() => flattenedGates.value.length)

// Methods
const fetchPRDetails = async () => {
  try {
    loading.value = true
    error.value = null

    logger.log('Fetching PR details:', { repository: repository.value, prNumber: prNumber.value })

    const response = await qualityGatesService.fetchPRDetails(repository.value, prNumber.value)
    prDetails.value = response

    logger.log('PR details fetched:', response)
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to fetch PR details'
    logger.error('Error fetching PR details:', err)
  } finally {
    loading.value = false
  }
}

const refreshDetails = () => {
  fetchPRDetails()
}

const toggleGate = (key: string) => {
  if (expandedGates.value.has(key)) {
    expandedGates.value.delete(key)
  } else {
    expandedGates.value.add(key)
  }
  // Force reactivity
  expandedGates.value = new Set(expandedGates.value)
}

const handleRetrigger = async () => {
  try {
    retriggering.value = true

    await qualityGatesService.retriggerPR(repository.value, prNumber.value)

    showRetriggerModal.value = false

    // Show success message (could use a toast here)
    logger.log('Re-trigger successful')
  } catch (err) {
    logger.error('Failed to re-trigger:', err)
    // Could show error toast
  } finally {
    retriggering.value = false
  }
}

// Watch for expand all toggle
watch(expandAll, (newValue) => {
  if (newValue) {
    expandedGates.value = new Set(flattenedGates.value.map((g) => g.key))
  } else {
    expandedGates.value = new Set()
  }
})

// Lifecycle
onMounted(() => {
  fetchPRDetails()
})
</script>
